import {
  useInformationTranslation,
  InformationTranslation,
} from '../../hooks/useInformationTranslations';
import { useSelector } from 'react-redux';
import { getInformationSelector } from '../../redux/selectors';
import { useCompany, useCompanyCanonicalUrl } from '@/hooks/useCompany';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Button } from '@simplywallst/ui-core/next';
import { getQueryKey, queryFn } from './query';
export function FinancialData({ name }: { name: string }) {
  const [showAllAnalyst, setShowAllAnalysts] = useState(false);
  const t = useInformationTranslation();

  const information = useSelector((state) => getInformationSelector(state, {}));

  const companyId = useCompanyCanonicalUrl();

  const company = useCompany(companyId);

  const analystCount = company.data?.data.analysis?.data.analyst_count;

  const { data, status } = useQuery({
    queryKey: getQueryKey(companyId),
    queryFn,
    enabled: typeof companyId === 'string' && companyId !== '',
  });

  const analysts = useMemo(() => {
    if (status === 'success') {
      const analystMap: { name: string; brokerage: string }[] = [];
      data.forEach((analystCoverage) => {
        const { data: brokers } = analystCoverage.brokers;
        brokers.forEach((broker) => {
          broker.analysts.data.forEach((analyst) => {
            analystMap.push({
              name: `${analyst.first_name} ${analyst.last_name}`,
              brokerage: broker.broker_name,
            });
          });
        });
      });
      return analystMap;
    }
    return [];
  }, [data, status]);

  const displayedAnalysts = showAllAnalyst ? analysts : analysts.slice(0, 3);

  return (
    <section className="rounded-lg bg-surface-1 p-x2 md:p-x4">
      <h1 className="m-0 text-2xl font-strong text-solid">
        {t('information.financialData.title')}
      </h1>
      <div className="overflow-x-auto pt-x4">
        <table className="border w-full table-auto border-collapse border-solid border-p-white-100/5">
          <thead className="bg-surface-2">
            <tr className="border-0 border-b">
              <th className="border-b px-4 py-2 text-left">
                <span className="text-xs font-strong text-solid">
                  {t('information.financialData.table.heading.data')}
                </span>
              </th>
              <th className="border-b px-4 py-2 text-right">
                <span className="text-xs font-strong text-solid">
                  {t('information.financialData.table.heading.lastUpdated')}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border border-p-white-100/5">
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.financialData.table.row1')}
                </span>
              </td>
              <td className="px-4 py-2 text-right">
                <span className="text-xs text-solid">
                  {information.lastAnalysisUpdateFormattedUTC}
                </span>
              </td>
            </tr>
            <tr className="border border-p-white-100/5">
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.financialData.table.row2')}
                </span>
              </td>
              <td className="px-4 py-2 text-right">
                <span className="text-xs text-solid">
                  {information.lastEODPriceUpdateFormattedUTC}
                </span>
              </td>
            </tr>
            <tr className="border border-p-white-100/5">
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.financialData.table.row3')}
                </span>
              </td>
              <td className="px-4 py-2 text-right">
                <span className="text-xs text-solid">
                  {information.lastEarningsUpdateFormattedUTC}
                </span>
              </td>
            </tr>
            <tr className="border">
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.financialData.table.row4')}
                </span>
              </td>
              <td className="px-4 py-2 text-right">
                <span className="text-xs text-solid">
                  {information.lastAnnualEarninsUpdateFormattedUTC}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <h2 className="mt-x4 text-lg font-strong text-solid">
        {t('information.header.text2')}
      </h2>
      <p className="text-soft">
        <InformationTranslation i18nKey="information.description2">
          The data used in our company analysis is from
          <a
            href="https://www.spglobal.com/market-intelligence/en"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            S&P Global Market Intelligence LLC
          </a>
          . The following data is used in our analysis model to generate this
          report. Data is normalised which can introduce a delay from the
          original source being available.
        </InformationTranslation>
      </p>
      <div className="overflow-x-auto pt-x4">
        <table className="border w-full table-auto border-collapse border-solid border-p-white-100/5">
          <thead className="bg-surface-2">
            <tr className="border-0 border-b">
              <th className="border-b px-4 py-2 text-left">
                <span className="text-xs font-strong text-solid">
                  {t('information.dataSources.table.heading1')}
                </span>
              </th>
              <th className="border-b px-4 py-2 text-left">
                <span className="text-xs font-strong text-solid">
                  {t('information.dataSources.table.heading2')}
                </span>
              </th>
              <th className="border-b px-4 py-2 text-left">
                <span className="text-xs font-strong text-solid">
                  {t('information.dataSources.table.heading3')}
                </span>
              </th>
              <th className="border-b px-4 py-2 text-left">
                <span className="text-xs font-strong text-solid">
                  <InformationTranslation i18nKey="information.dataSources.table.heading4">
                    Example US Source<sup>*</sup>
                  </InformationTranslation>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border border-p-white-100/5 align-top">
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.dataSources.table.row1.column1')}
                </span>
              </td>
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.dataSources.table.row1.column2')}
                </span>
              </td>
              <td className="px-4 py-2">
                <ul className="list-disc pl-4">
                  <li>
                    <span className="text-xs text-solid">
                      {t('information.dataSources.table.row1.bullet1')}
                    </span>
                  </li>
                  <li>
                    <span className="text-xs text-solid">
                      {t('information.dataSources.table.row1.bullet2')}
                    </span>
                  </li>
                  <li>
                    <span className="text-xs text-solid">
                      {t('information.dataSources.table.row1.bullet3')}
                    </span>
                  </li>
                </ul>
              </td>
              <td className="px-4 py-2">
                <ul className="list-disc pl-4">
                  <li>
                    <a
                      href="https://www.sec.gov/cgi-bin/browse-edgar?company=&CIK=&type=10-K&owner=include&count=40&action=getcurrent"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-xs"
                    >
                      {t('information.dataSources.10k')}
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.sec.gov/cgi-bin/browse-edgar?company=&CIK=&type=10-Q&owner=include&count=40&action=getcurrent"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-xs"
                    >
                      {t('information.dataSources.10q')}
                    </a>
                  </li>
                </ul>
              </td>
            </tr>
            <tr className="border border-p-white-100/5 align-top">
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.dataSources.table.row2.column1')}
                </span>
              </td>
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.dataSources.table.row2.column2')}
                </span>
              </td>
              <td className="px-4 py-2">
                <ul className="list-disc pl-4">
                  <li>
                    <span className="text-xs text-solid">
                      {t('information.dataSources.table.row2.bullet1')}
                    </span>
                  </li>
                  <li>
                    <span className="text-xs text-solid">
                      {t('information.dataSources.table.row2.bullet2')}
                    </span>
                  </li>
                </ul>
              </td>
              <td className="px-4 py-2">
                <ul className="list-disc pl-4">
                  <li>
                    <span className="text-xs text-solid">
                      {t('information.dataSources.analysts')}
                    </span>
                  </li>
                  <li>
                    <a
                      href="https://bluematrix.com/www3/Home.action"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-xs"
                    >
                      Blue Matrix
                    </a>
                  </li>
                </ul>
              </td>
            </tr>
            <tr className="border border-p-white-100/5 align-top">
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.dataSources.table.row3.column1')}
                </span>
              </td>
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.dataSources.table.row3.column2')}
                </span>
              </td>
              <td className="px-4 py-2">
                <ul className="list-disc pl-4">
                  <li>
                    <span className="text-xs text-solid">
                      {t('information.dataSources.table.row3.bullet1')}
                    </span>
                  </li>
                  <li>
                    <span className="text-xs text-solid">
                      {t('information.dataSources.table.row3.bullet2')}
                    </span>
                  </li>
                </ul>
              </td>
              <td className="px-4 py-2">
                <ul className="list-disc pl-4">
                  <li>
                    <a
                      href="https://www.ice.com/fixed-income-data-services/data-and-analytics/exchange-data"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-xs"
                    >
                      {t('information.dataSources.ice')}
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.sec.gov/cgi-bin/browse-edgar?company=&CIK=&type=S-1&owner=include&count=40&action=getcurrent"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-xs"
                    >
                      {t('information.dataSources.s1')}
                    </a>
                  </li>
                </ul>
              </td>
            </tr>
            <tr className="border border-p-white-100/5 align-top">
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.dataSources.table.row4.column1')}
                </span>
              </td>
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.dataSources.timeframe.10years')}
                </span>
              </td>
              <td className="px-4 py-2">
                <ul className="list-disc pl-4">
                  <li className="text-xs text-solid">
                    {t('information.dataSources.table.row4.bullet1')}
                  </li>
                  <li className="text-xs text-solid">
                    {t('information.dataSources.table.row4.bullet2')}
                  </li>
                </ul>
              </td>
              <td className="px-4 py-2">
                <ul className="list-disc pl-4">
                  <li>
                    <a
                      href="https://www.sec.gov/cgi-bin/browse-edgar?company=&CIK=&type=4&owner=include&count=40&action=getcurrent"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-xs"
                    >
                      {t('information.dataSources.form4')}
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.sec.gov/cgi-bin/browse-edgar?company=&CIK=&type=SC+13D&owner=include&count=40&action=getcurrent"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-xs"
                    >
                      {t('information.dataSources.13d')}
                    </a>
                  </li>
                </ul>
              </td>
            </tr>
            <tr className="border border-p-white-100/5 align-top">
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.dataSources.table.row5.column1')}
                </span>
              </td>
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.dataSources.timeframe.10years')}
                </span>
              </td>
              <td className="px-4 py-2">
                <ul className="list-disc pl-4">
                  <li className="text-xs text-solid">
                    {t('information.dataSources.table.row5.bullet1')}
                  </li>
                  <li className="text-xs text-solid">
                    {t('information.dataSources.table.row5.bullet2')}
                  </li>
                </ul>
              </td>
              <td className="px-4 py-2">
                <ul className="list-disc pl-4">
                  <li>
                    <a
                      href="https://www.sec.gov/cgi-bin/browse-edgar?company=&CIK=&type=10-K&owner=include&count=40&action=getcurrent"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-xs"
                    >
                      {t('information.dataSources.10k')}
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.sec.gov/cgi-bin/browse-edgar?company=&CIK=&type=DEF+14A&owner=include&count=40&action=getcurrent"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-xs"
                    >
                      {t('information.dataSources.14a')}
                    </a>
                  </li>
                </ul>
              </td>
            </tr>
            <tr className="align-top">
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.dataSources.table.row6.column1')}
                </span>
              </td>
              <td className="px-4 py-2">
                <span className="text-xs text-solid">
                  {t('information.dataSources.timeframe.10years')}
                </span>
              </td>
              <td className="px-4 py-2">
                <ul className="list-disc pl-4">
                  <li className="text-xs text-solid">
                    {t('information.dataSources.table.row6.bullet1')}
                  </li>
                </ul>
              </td>
              <td className="px-4 py-2">
                <ul className="list-disc pl-4">
                  <li>
                    <a
                      href="https://www.sec.gov/cgi-bin/browse-edgar?company=&CIK=&type=8-K&owner=include&count=40&action=getcurrent"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-xs"
                    >
                      {t('information.dataSources.8k')}
                    </a>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="py-x3 text-right text-tiny text-soft">
        <InformationTranslation i18nKey="information.dataSources.footnote">
          <sup>*</sup> example for US securities, for non-US equivalent
          regulatory forms and sources are used.
        </InformationTranslation>
      </p>

      <InformationTranslation i18nKey="information.dataSources.description">
        <p className="text-soft">
          Unless specified all financial data is based on a yearly period but
          updated quarterly. This is known as Trailing Twelve Month (TTM) or
          Last Twelve Month (LTM) Data. Learn more
          <a
            href="https://support.simplywall.st/hc/en-us/articles/115006170908-Your-data-is-different-from-other-reports-and-websites-why-is-that"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            here
          </a>
          .
        </p>
      </InformationTranslation>

      <h2 className="mt-x4 text-lg font-strong text-solid">
        {t('information.header.text3')}
      </h2>
      <InformationTranslation i18nKey="information.analysisModel.description">
        <p className="text-soft">
          Details of the analysis model used to generate this report is
          available on our
          <a
            href="https://github.com/SimplyWallSt/Company-Analysis-Model/blob/master/MODEL.markdown"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            Github page
          </a>
          , we also have
          <a
            href="https://support.simplywall.st/hc/en-us/categories/360000146555"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            guides
          </a>
          on how to use our reports and
          <a
            href="https://www.youtube.com/watch?v=BUCa_h3YYWY&list=PLVdrK24l-IHfQ-JGbz5N1BZ9EcpPdkpLP"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            tutorials on Youtube
          </a>
          .
        </p>
      </InformationTranslation>
      <p className="text-soft">
        <InformationTranslation i18nKey="information.analysisModel.learnMore">
          Learn about the <a href="/our-people">world class team</a> who
          designed and built the Simply Wall St analysis model.
        </InformationTranslation>
      </p>
      <h2 className="mt-x4 text-lg font-strong text-solid">
        {t('information.header.text4')}
      </h2>
      <InformationTranslation i18nKey="information.industrySector.description">
        <p className="text-soft">
          Our industry and section metrics are calculated every 6 hours by
          Simply Wall St, details of our process are available on{' '}
          <a
            href="https://github.com/SimplyWallSt/Company-Analysis-Model/blob/master/industry_averages.markdown"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            Github
          </a>
          .
        </p>
      </InformationTranslation>
      <h2 className="mt-x4 text-lg font-strong text-solid">
        {t('information.header.text5')}
      </h2>
      <p className="text-soft">
        {t('information.analystSources.description', {
          name,
          total: analysts.length,
          count: analystCount,
        })}
      </p>
      <div className="overflow-x-auto pt-x4">
        <table className="border w-full table-auto border-collapse border-solid border-p-white-100/5">
          <thead className="bg-surface-2">
            <tr className="border-0 border-b">
              <th className="border-b px-4 py-2 text-left">
                <span className="text-xs font-strong text-solid">
                  {t('information.analystSources.table.heading1')}
                </span>
              </th>
              <th className="border-b px-4 py-2 text-right">
                <span className="text-xs font-strong text-solid">
                  {t('information.analystSources.table.heading2')}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {displayedAnalysts.map((analyst, index) => {
              if (!showAllAnalyst && index >= 3) {
                return null;
              }
              return (
                <tr
                  key={`${analyst.name} ${analyst.brokerage}`}
                  className="border border-p-white-100/5"
                >
                  <td className="px-4 py-2">
                    <span className="text-xs text-solid">{analyst.name}</span>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <span className="text-xs text-solid">
                      {analyst.brokerage}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Button
          variant="outline"
          className="mt-4 w-full"
          onClick={() => setShowAllAnalysts((state) => !state)}
          aria-expanded={showAllAnalyst}
        >
          {showAllAnalyst
            ? t('information.analystSources.table.collapse')
            : t('information.analystSources.table.expand', {
                count: analysts.length - displayedAnalysts.length,
              })}
        </Button>
      </div>
    </section>
  );
}
